import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MessageSquarePlus, RefreshCw, ChevronLeft, ChevronRight, X,
  Bug, Lightbulb, HelpCircle, Mail, Calendar, Image, AlertCircle, ExternalLink,
} from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import './GarageFeedbacksAdminPanel.css';

const TYPE_FILTERS = [
  { value: '', label: 'All Types' },
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature' },
  { value: 'other', label: 'Other' },
];

const RELATED_FILTERS = [
  { value: '', label: 'All Areas' },
  { value: 'discover', label: 'Discover' },
  { value: 'requests', label: 'Requests' },
  { value: 'admin', label: 'Admin' },
  { value: 'launch', label: 'Launch / RC App' },
  { value: 'roi', label: 'Dashboard / ROI' },
  { value: 'access', label: 'Access' },
  { value: 'other', label: 'Other' },
];

const TYPE_META = {
  bug: { label: 'Bug', Icon: Bug, cls: 'gfap-badge-bug' },
  feature: { label: 'Feature', Icon: Lightbulb, cls: 'gfap-badge-feature' },
  other: { label: 'Other', Icon: HelpCircle, cls: 'gfap-badge-other' },
};

const RELATED_LABELS = Object.fromEntries(
  RELATED_FILTERS.filter((f) => f.value).map((f) => [f.value, f.label])
);

const formatDate = (iso) => {
  if (!iso) return 'N/A';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const DetailModal = ({ feedback, onClose }) => {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [feedback?.id, feedback?.screenshotUrl]);

  if (!feedback) return null;
  const type = TYPE_META[feedback.feedbackType] || {
    label: feedback.feedbackType,
    Icon: HelpCircle,
    cls: 'gfap-badge-other',
  };
  const screenshotUrl = (feedback.screenshotUrl || '').trim();

  return (
    <div className="gfap-modal-overlay" onClick={onClose}>
      <div className="gfap-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gfap-modal-header">
          <h2 className="gfap-modal-title">Feedback Detail</h2>
          <button type="button" className="gfap-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="gfap-modal-body">
          <div className="gfap-detail-meta">
            <span className={`gfap-badge ${type.cls}`}>
              <type.Icon size={12} /> {type.label}
            </span>
            {feedback.relatedTo && (
              <span className="gfap-detail-related">
                {RELATED_LABELS[feedback.relatedTo] || feedback.relatedTo}
              </span>
            )}
            <span className="gfap-detail-date">
              <Calendar size={13} /> {formatDate(feedback.createdAt)}
            </span>
          </div>

          <div className="gfap-detail-section">
            <span className="gfap-detail-label">
              <Mail size={13} /> User
            </span>
            <p className="gfap-detail-value">
              {feedback.userName ? `${feedback.userName} · ` : ''}
              {feedback.userEmail || '—'}
            </p>
          </div>

          <div className="gfap-detail-section">
            <span className="gfap-detail-label">
              {feedback.feedbackType === 'bug' ? 'What went wrong?' : 'What is your idea?'}
            </span>
            <p className="gfap-detail-value">{feedback.comments}</p>
          </div>

          {feedback.outputComments && (
            <div className="gfap-detail-section">
              <span className="gfap-detail-label">What did you expect?</span>
              <p className="gfap-detail-value">{feedback.outputComments}</p>
            </div>
          )}

          <div className="gfap-detail-section">
            <span className="gfap-detail-label">
              <Image size={13} /> Screenshot
            </span>
            {!screenshotUrl ? (
              <div className="gfap-screenshot-fallback">
                <AlertCircle size={20} />
                <span>No screenshot was attached to this feedback.</span>
              </div>
            ) : imgFailed ? (
              <div className="gfap-screenshot-fallback">
                <AlertCircle size={20} />
                <span>Could not load screenshot.</span>
                <a href={screenshotUrl} target="_blank" rel="noopener noreferrer" className="gfap-screenshot-link">
                  Open URL <ExternalLink size={12} />
                </a>
              </div>
            ) : (
              <div className="gfap-detail-screenshot">
                <a href={screenshotUrl} target="_blank" rel="noopener noreferrer" title="Open full size">
                  <img
                    src={screenshotUrl}
                    alt="Feedback screenshot"
                    referrerPolicy="no-referrer"
                    onError={() => setImgFailed(true)}
                  />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const GarageFeedbacksAdminPanel = () => {
  const [offset, setOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [feedbackType, setFeedbackType] = useState('');
  const [relatedTo, setRelatedTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [feedbacks, setFeedbacks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await garageAPI.listFeedbacks({
        feedbackType: feedbackType || undefined,
        relatedTo: relatedTo || undefined,
        limit: itemsPerPage,
        offset,
      });
      setFeedbacks(data.feedbacks || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load feedbacks');
      setFeedbacks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [feedbackType, relatedTo, offset, itemsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilter = (setter, val) => {
    setter(val);
    setOffset(0);
  };

  const filteredFeedbacks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return feedbacks;
    return feedbacks.filter((fb) => {
      const hay = [
        fb.comments,
        fb.outputComments,
        fb.userEmail,
        fb.userName,
        fb.feedbackType,
        fb.relatedTo,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [feedbacks, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const currentPage = Math.floor(offset / itemsPerPage) + 1;

  return (
    <div className="gfap-container">
      <div className="gfap-toolbar">
        <div className="gfap-toolbar-left">
          <MessageSquarePlus size={20} className="gfap-title-icon" />
          <div>
            <h3 className="gfap-title">User Feedbacks</h3>
            <p className="gfap-subtitle">Platform feedback from Garage users</p>
          </div>
        </div>
        <div className="gfap-toolbar-actions">
          <span className="gfap-count-badge">
            {total.toLocaleString()} feedback{total !== 1 ? 's' : ''}
          </span>
          <button type="button" className="gfap-refresh-btn" onClick={load} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'gfap-spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="gfap-filter-bar">
        <div className="gfap-filter-group">
          <span className="gfap-filter-label">Type</span>
          <div className="gfap-filter-pills">
            {TYPE_FILTERS.map(({ value, label }) => (
              <button
                key={value || 'all-type'}
                type="button"
                className={`gfap-pill ${feedbackType === value ? 'active' : ''}`}
                onClick={() => applyFilter(setFeedbackType, value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="gfap-filter-group">
          <span className="gfap-filter-label">Related To</span>
          <div className="gfap-filter-pills">
            {RELATED_FILTERS.map(({ value, label }) => (
              <button
                key={value || 'all-related'}
                type="button"
                className={`gfap-pill ${relatedTo === value ? 'active' : ''}`}
                onClick={() => applyFilter(setRelatedTo, value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search + pagination ABOVE the table */}
      <div className="gfap-controls">
        <input
          type="text"
          className="gfap-search"
          placeholder="Search feedbacks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="gfap-controls-right">
          <span className="gfap-results-count">
            {searchQuery.trim()
              ? `${filteredFeedbacks.length} of ${feedbacks.length} on this page`
              : `${total} results`}
          </span>
          <select
            className="gfap-page-size"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setOffset(0);
            }}
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
          {total > itemsPerPage && (
            <div className="gfap-pager gfap-pager-inline">
              <button
                type="button"
                disabled={offset <= 0}
                onClick={() => setOffset(Math.max(0, offset - itemsPerPage))}
              >
                <ChevronLeft size={16} />
              </button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={offset + itemsPerPage >= total}
                onClick={() => setOffset(offset + itemsPerPage)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {error && <div className="gfap-error">{error}</div>}

      <div className="gfap-table-wrap">
        {loading && feedbacks.length === 0 ? (
          <div className="gfap-empty">Loading…</div>
        ) : filteredFeedbacks.length === 0 ? (
          <div className="gfap-empty">No feedback yet.</div>
        ) : (
          <table className="gfap-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Related</th>
                <th>Comments</th>
                <th>Screenshot</th>
                <th>User</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredFeedbacks.map((fb) => {
                const type = TYPE_META[fb.feedbackType] || TYPE_META.other;
                return (
                  <tr key={fb.id} onClick={() => setSelected(fb)}>
                    <td>
                      <span className={`gfap-badge ${type.cls}`}>
                        <type.Icon size={12} /> {type.label}
                      </span>
                    </td>
                    <td>{RELATED_LABELS[fb.relatedTo] || fb.relatedTo || '—'}</td>
                    <td className="gfap-comments-cell">{fb.comments}</td>
                    <td>{fb.screenshotUrl ? 'Yes' : '—'}</td>
                    <td>{fb.userEmail || '—'}</td>
                    <td>{formatDate(fb.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <DetailModal feedback={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default GarageFeedbacksAdminPanel;
